import { AuthContext } from '@repo-level/core-front';
import { ImageComponent } from 'components/Image';
import { ProgressBarPalette } from 'components/progressBars/helpers';
import { SmallProgressBar } from 'components/progressBars/SmallProgressBar';
import { BonanzaContext } from 'contexts/bonanza';
import { BonanzaStatus } from 'helpers/bonanza';
import { useTheme } from 'hooks/theme';
import { PERCENT_BREACKPOINTS_BONANZA } from 'modules/bonanza/components/BoxSection/helpers';
import { useBonanzaLogic } from 'modules/bonanza/hooks/useBonanzaLogic';
import type { FC } from 'react';
import { useContext, useMemo } from 'react';

export interface AddonsProps {
  isMobile: boolean;
}

export const Addons: FC<AddonsProps> = ({ isMobile }) => {
  const { bonanzaReward, bonanzaData, bonanzaLevelItem, userBonanzaGroup } =
    useContext(BonanzaContext);
  const { isAuthenticated } = useContext(AuthContext);
  const { images } = useTheme();
  const { nextIterationStartDay, isBonusIssued, isBonusLocked } =
    useBonanzaLogic();

  const imgSrc = useMemo(() => {
    if (!bonanzaLevelItem) return '';
    const { labelMob, label } = bonanzaLevelItem;
    if (isAuthenticated) return isMobile ? labelMob?.url : label?.url;
    return isMobile ? bonanzaData?.menu?.unloggedMob.url : label?.url;
  }, [isAuthenticated, isMobile, bonanzaLevelItem, bonanzaData]);

  const imgConfig = useMemo(
    () => ({
      src: imgSrc || '',
      alt: 'logo',
      width: isMobile ? 56 : 30,
      height: isMobile ? 56 : 30,
    }),
    [isMobile, imgSrc]
  );

  const {
    yourProgress,
    yourReward,
    unLoggedLabelText,
    returnToStartFresh,
    openBonus,
  } = bonanzaData?.menu ?? {};

  const statusText = useMemo(() => {
    if (!isAuthenticated) return unLoggedLabelText;
    const textByStatus: Partial<Record<BonanzaStatus, string | undefined>> = {
      [BonanzaStatus.PROMO_LOCKED]: returnToStartFresh?.replace(
        '{{day}}',
        nextIterationStartDay ?? ''
      ),
      [BonanzaStatus.BONUS_ISSUED]: openBonus,
    };
    return textByStatus[userBonanzaGroup];
  }, [
    isAuthenticated,
    unLoggedLabelText,
    returnToStartFresh,
    nextIterationStartDay,
    openBonus,
    userBonanzaGroup,
  ]);

  const isPromoAvailable = isAuthenticated && !isBonusLocked && !isBonusIssued;

  if (!(bonanzaData && bonanzaLevelItem)) return null;

  return (
    <div className="info">
      <div className="addons">
        {imgSrc && (
          <div className="top">
            <p className="title">{bonanzaData.title}</p>
            <ImageComponent {...imgConfig} />
          </div>
        )}
        <div className="content">
          {isMobile ? (
            <>
              {statusText && <p className="status-text">{statusText}</p>}
              {isPromoAvailable && (
                <>
                  <span>
                    {yourProgress}: {bonanzaLevelItem.progressPercent}%
                  </span>
                  <span>
                    {yourReward}: {bonanzaReward}
                  </span>
                  <SmallProgressBar
                    config={{
                      dotsCount: PERCENT_BREACKPOINTS_BONANZA.length,
                      color: ProgressBarPalette.PURPLE,
                    }}
                    userProgress={bonanzaLevelItem.progressPercent}
                  />
                </>
              )}
            </>
          ) : (
            statusText && <p className="status-text">{statusText}</p>
          )}
        </div>
        {isMobile && <images.arrowRightSmallIcon.component />}
      </div>
    </div>
  );
};
